import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<string>(
    startDate ? formatDateForInput(startDate) : ''
  );
  const [tempEndDate, setTempEndDate] = useState<string>(
    endDate ? formatDateForInput(endDate) : ''
  );

  const [tempStartHour, setTempStartHour] = useState('12');
  const [tempStartMinute, setTempStartMinute] = useState('00');
  const [tempStartAMPM, setTempStartAMPM] = useState('AM');

  const [tempEndHour, setTempEndHour] = useState('12');
  const [tempEndMinute, setTempEndMinute] = useState('00');
  const [tempEndAMPM, setTempEndAMPM] = useState('AM');

  // Track which quick select is active (7, 30, 90, or null for custom)
  const [selectedQuickRange, setSelectedQuickRange] = useState<number | null>(null);

  const open = Boolean(anchorEl);

  function formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateForDisplay(date: Date): string {
    if (!date) return '';

    // Format date: "Jan 21, 2026"
    const datePart = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });

    // Format time: "03:30 PM"
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `${datePart} ${timePart}`;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Initialize temp values when opening
    setTempStartDate(startDate ? formatDateForInput(startDate) : '');
    setTempEndDate(endDate ? formatDateForInput(endDate) : '');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function buildDate(
    date: string,
    hour: string,
    minute: string,
    ampm: string
  ): Date | null {
    if (!date) return null;

    let h = parseInt(hour, 10);
    const m = parseInt(minute, 10);

    // Convert 12-hour to 24-hour
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;

    const [year, month, day] = date.split('-').map(Number);

    return new Date(year, month - 1, day, h, m);
  }


  const handleApply = () => {
    const start = buildDate(tempStartDate, tempStartHour, tempStartMinute, tempStartAMPM);
    const end = buildDate(tempEndDate, tempEndHour, tempEndMinute, tempEndAMPM);
    
    onDateRangeChange(start, end);
    handleClose();
  };

  const handleClear = () => {
    setTempStartDate('');
    setTempEndDate('');
    setSelectedQuickRange(null);
    onDateRangeChange(null, null);
    handleClose();
  };

  const setQuickRange = (days: number) => {
    // GeoNeutron API has 24 hour data lag - set end date to 1 day ago
    const end = new Date();
    end.setDate(end.getDate() - 1); // Subtract 1 day for data lag
    
    const start = new Date(end);
    start.setDate(start.getDate() - days);
    
    setTempStartDate(formatDateForInput(start));
    setTempEndDate(formatDateForInput(end));
    setSelectedQuickRange(days); // Track which quick select is active
  };

  // Clear quick range selection when user manually changes dates
  const handleStartDateChange = (value: string) => {
    setTempStartDate(value);
    setSelectedQuickRange(null);
  };

  const handleEndDateChange = (value: string) => {
    setTempEndDate(value);
    setSelectedQuickRange(null);
  };

  const getDateRangeDisplay = () => {
    if (!startDate && !endDate) return null;
    if (startDate && endDate) {
      return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
    }
    if (startDate) {
      return `From ${formatDateForDisplay(startDate)}`;
    }
    if (endDate) {
      return `Until ${formatDateForDisplay(endDate)}`;
    }
    return null;
  };

  const dateRangeText = getDateRangeDisplay();

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: 'white',
          position: 'relative',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <CalendarIcon />
        {(startDate || endDate) && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.light',
              border: '2px solid #03111E',
            }}
          />
        )}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 500,
            p: 2,
          },
        }}
      >
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Select Date Range
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Current Selection Display */}
          {dateRangeText && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={dateRangeText}
                onDelete={handleClear}
                color="primary"
                variant="outlined"
                sx={{ width: '100%', justifyContent: 'space-between' }}
              />
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Quick Select Buttons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Quick Select
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                size="small" 
                variant={selectedQuickRange === 7 ? "contained" : "outlined"} 
                onClick={() => setQuickRange(7)}
              >
                Last 7 Days
              </Button>
              <Button 
                size="small" 
                variant={selectedQuickRange === 30 ? "contained" : "outlined"} 
                onClick={() => setQuickRange(30)}
              >
                Last 30 Days
              </Button>
              <Button 
                size="small" 
                variant={selectedQuickRange === 90 ? "contained" : "outlined"} 
                onClick={() => setQuickRange(90)}
              >
                Last 90 Days
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
              Note: End date automatically adjusted for 24hr data lag
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Date Inputs */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* START */}
            <Typography variant="caption">Start Date & Time</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Date Input - smaller */}
              <TextField
                label="Date"
                type="date"
                size="small"
                value={tempStartDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 175 }}
              />

              {/* Hour Dropdown - bigger */}
              <TextField
                select
                label="Hour"
                size="small"
                value={tempStartHour}
                onChange={(e) => setTempStartHour(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 80 }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </TextField>

              {/* Minute Dropdown */}
              <TextField
                select
                label="Minute"
                size="small"
                value={tempStartMinute}
                onChange={(e) => setTempStartMinute(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 80 }}
              >
                {['00', '15', '30', '45'].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </TextField>

              {/* AM/PM Dropdown */}
              <TextField
                select
                label="AM/PM"
                size="small"
                value={tempStartAMPM}
                onChange={(e) => setTempStartAMPM(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 80 }}
              >
                {['AM', 'PM'].map((ampm) => (
                  <option key={ampm} value={ampm}>
                    {ampm}
                  </option>
                ))}
              </TextField>
            </Box>

            {/* END */}
            <Typography variant="caption">End Date & Time</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Date"
                type="date"
                size="small"
                value={tempEndDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 175 }}
              />
              <TextField
                select
                label="Hour"
                size="small"
                value={tempEndHour}
                onChange={(e) => setTempEndHour(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 80 }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="Minute"
                size="small"
                value={tempEndMinute}
                onChange={(e) => setTempEndMinute(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 80 }}
              >
                {['00', '15', '30', '45'].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="AM/PM"
                size="small"
                value={tempEndAMPM}
                onChange={(e) => setTempEndAMPM(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ width: 80 }}
              >
                {['AM', 'PM'].map((ampm) => (
                  <option key={ampm} value={ampm}>
                    {ampm}
                  </option>
                ))}
              </TextField>
            </Box>
          </Box>
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={handleClear} color="inherit">
              Clear
            </Button>
            <Button onClick={handleApply} variant="contained">
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePicker;